import { useAuth0 } from '@auth0/auth0-react'
import { useElements, useStripe } from '@stripe/react-stripe-js'
import dayjs from 'dayjs'
import { FormikContextType, getIn, useFormikContext } from 'formik'
import { useRouter } from 'next/router'
import pluralize from 'pluralize'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { IoInformationCircle } from 'react-icons/io5'

import { useAuth } from 'src/providers/AuthProvider'
import { NotificationType, notify } from 'src/providers/NotificationProvider'
import { usePayment } from 'src/providers/PaymentProvider'
import { useStepper } from 'src/providers/StepperProvider'
import { useUser } from 'src/providers/UserProvider'

import { Currency } from 'src/components/atoms/Currency'
import { InternalLink, OnClickLink } from 'src/components/atoms/Link'
import { Note } from 'src/components/atoms/Note'
import { Tooltip } from 'src/components/atoms/Tooltip'
import { TypographyRebrand } from 'src/components/atoms/TypographyRebrand'
import { FormField } from 'src/components/molecules/FormField'
import { PaymentContactDetails } from 'src/components/molecules/PaymentContactDetails'
import { StepperButtons } from 'src/components/molecules/StepperButtons'
import { StripePaymentElements } from 'src/components/organisms/StripePaymentElements'
import {
  DonateFormValues,
  DonateFrequencyTab
} from 'src/components/organisms/forms/DonateForm/types'

import {
  CreatePaymentInput,
  namedOperations,
  useCreateGuestPaymentMutation,
  useCreatePaymentMutation,
  useGetRegionByIdLazyQuery,
  useGetRegistryByIdQuery,
  useUpdatePurchaseErrorMutation,
  useGetAccountSubscriptionDetailsQuery
} from 'src/config/generated/graphql'
import type {
  CreateGuestPaymentInput,
  CreateGuestPaymentMutation,
  CreateGuestPaymentOutput,
  CreatePaymentMutation,
  CreatePaymentOutput,
  InvoiceAddress
} from 'src/config/generated/graphql'
import {
  viewAccountBillingDetailsPath,
  paymentSuccessPath
} from 'src/config/paths'
import { env } from 'src/env/client.mjs'
import { useCache } from 'src/hooks/useCache'
import { useProductPrice } from 'src/hooks/useProductPrice'
import { FormFieldTypes } from 'src/types/form'
import {
  PaymentMethod as PaymentMethodEnum,
  PurchaseType
} from 'src/types/purchase'
import { EventName, trackEvent } from 'src/utils/ga'
import { getStoredUtmInput } from 'src/utils/utm'

const INVOICE_PAYMENT_MINIMUM_AMOUNT = 99

const waitForRecaptcha = (
  getExecute: () => ((action?: string) => Promise<string>) | undefined,
  timeoutMs = 5000
): Promise<(action?: string) => Promise<string>> =>
  new Promise((resolve, reject) => {
    const fn = getExecute()
    if (fn) {
      resolve(fn)
      return
    }
    const interval = setInterval(() => {
      const execute = getExecute()
      if (execute) {
        clearInterval(interval)
        resolve(execute)
      }
    }, 100)
    setTimeout(() => {
      clearInterval(interval)
      reject(
        new Error(
          'Payment verification failed to load. This is usually caused by an ad blocker — please disable it, refresh the page, and try again.'
        )
      )
    }, timeoutMs)
  })

export const Payment = () => {
  const { validateStep, onSuccess } = useStepper()
  const { user, memberships } = useUser()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const executeRecaptchaRef = useRef(executeRecaptcha)
  useEffect(() => {
    executeRecaptchaRef.current = executeRecaptcha
  }, [executeRecaptcha])
  const [purchase, setPurchase] = useState<
    CreatePaymentOutput | CreateGuestPaymentOutput | undefined
  >(undefined)

  const formikContext: FormikContextType<DonateFormValues> = useFormikContext()
  const { isSubmitting, setFieldValue, values } = formikContext

  const guestDetailsRef = useRef({ email: '', organisation: '', optIn: false })
  guestDetailsRef.current = {
    email: values.ReceiptEmail,
    organisation: values.Organisation,
    optIn: !!values.CommsOptIn
  }

  // They shouldn't get here, but just in case
  const { isAuthenticated, loginWithRedirect } = useAuth0()
  const { login } = useAuth()

  const router = useRouter()

  // A subscription is set up for monthly donations (legacy + monthly packages)
  // AND annual packages — both are recurring. The existing-subscription lookup,
  // force-login and "you already have one" note all key off this so annual gets
  // the same one-sub-per-funder guard as monthly.
  const isAnnual = values.DonationType === DonateFrequencyTab.Annual
  const isRecurring =
    values.DonationType === DonateFrequencyTab.Monthly || isAnnual

  const { data: subscription } = useGetAccountSubscriptionDetailsQuery({
    variables: {
      funderId: user?.Account?.FunderId!
    },
    skip: !isRecurring
  })

  useEffect(() => {
    if (!isAuthenticated && isRecurring) {
      loginWithRedirect({
        screen_hint: 'signup',
        appState: { returnTo: router.asPath }
      })
    }
  }, [isAuthenticated, loginWithRedirect, isRecurring, subscription, router])

  const totalTrees = useMemo(
    () => parseInt(`${values.QuantityOther}`) || parseInt(`${values.Quantity}`),
    [values.Quantity, values.QuantityOther]
  )

  const { productPrice } = useProductPrice(totalTrees, !isRecurring)
  const [getRegionById, { data: regionData }] = useGetRegionByIdLazyQuery()

  const { setValue: setDonateFormCache } = useCache({
    key: 'donateForm'
  })

  const isRegistryGiftForm = !!getIn(values, 'RegistryId')

  const regionName = useMemo(() => {
    return regionData?.Region[0]
      ? [regionData.Region[0].Name, regionData.Region[0].AltName]
          .filter(Boolean)
          .join(' - ')
      : 'Where trees are needed most'
  }, [regionData?.Region])

  const stripe = useStripe()
  const elements = useElements()
  const { processing, succeeded, setProcessing, setSucceeded } = usePayment()
  const [walletPay, setWalletPay] = useState(false)

  const isSubmitDisabled = !!(
    isSubmitting ||
    (values.PaymentMethod === PaymentMethodEnum.CreditCard && !stripe) ||
    (values.PaymentMethod === PaymentMethodEnum.CreditCard && !elements) ||
    processing ||
    succeeded
  )

  const [createPayment] = useCreatePaymentMutation({
    refetchQueries: [namedOperations.Query.getAccountSubscriptionDetails]
  })
  const [createGuestPayment] = useCreateGuestPaymentMutation({
    refetchQueries: [namedOperations.Query.getAccountSubscriptionDetails]
  })
  const [updatePurchaseError] = useUpdatePurchaseErrorMutation()
  const { data: registry } = useGetRegistryByIdQuery({
    variables: {
      id: getIn(values, 'RegistryId')
    },
    skip: !isRegistryGiftForm
  })

  const nextBillingDate = dayjs(Date.now())
    .add(1, isAnnual ? 'year' : 'month')
    .format('DD/MM/YYYY')

  useEffect(() => {
    const regionId = isRegistryGiftForm
      ? registry?.Registry_by_pk?.RegionId
      : values.PlantingRegionId

    if (regionId) getRegionById({ variables: { regionId } })
  }, [getRegionById, isRegistryGiftForm, registry, values.PlantingRegionId])

  const sendTrackingEvent = (eventName: EventName) => {
    trackEvent(eventName, {
      currency: 'NZD',
      value: Number(values.Amount),
      payment_type: values.PaymentMethod,
      donationType: values.DonationType,
      items: [
        {
          item_id: `trees-${values.QuantityOther || values.Quantity}`,
          item_name: `${values.QuantityOther || values.Quantity} ${pluralize(
            'tree',
            Number(values.QuantityOther || values.Quantity)
          )}`,
          item_list_name: 'Tree',
          item_category: 'Donate',
          quantity: values.QuantityOther || values.Quantity,
          price:
            Number(values.Amount) /
            Number(values.QuantityOther || values.Quantity),
          index: 0
        }
      ]
    })
  }

  const createGuestPaymentInput = useCallback(
    async (input: CreatePaymentInput): Promise<CreateGuestPaymentInput> => {
      const { ReceiptEmail, ...rest } = input
      delete rest.SavePaymentMethod

      const execute = await waitForRecaptcha(() => executeRecaptchaRef.current)

      return {
        ...rest,
        GuestEmail: ReceiptEmail,
        RecaptchaToken: await execute()
      }
    },
    []
  )

  const sendGuestDetailsToHubspot = () => {
    const { email, organisation, optIn } = guestDetailsRef.current
    if (!email) return
    const org = organisation?.trim()
    fetch('/api/hubspot/guest-contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        email,
        organisation: org || undefined,
        optIn
      })
    }).catch(() => {})
  }

  const handleCreditCardPayment = useCallback(
    async (input: CreatePaymentInput) => {
      const onError = (error: any) => {
        setProcessing(false)
        notify({ message: error.message!, type: NotificationType.error })
      }

      const onCompleted = async (
        response:
          | CreatePaymentMutation['create_payment']
          | CreateGuestPaymentMutation['create_guest_payment']
      ) => {
        if (response?.PaymentIntentClientSecret) {
          setPurchase(response)
          const paymentMethodParams =
            input.PaymentMethod === 'SavedCreditCard'
              ? {}
              : { elements: elements! }
          const result = await stripe!.confirmPayment({
            ...paymentMethodParams,
            clientSecret: response.PaymentIntentClientSecret,
            confirmParams: {
              payment_method_data: {
                billing_details: {
                  name: input.ReceiptName
                }
              },
              return_url: `${env.NEXT_PUBLIC_APP_DOMAIN}${paymentSuccessPath({
                queryParams: {
                  token: response.Token!,
                  hash: response.Hash!,
                  purchaseId: response.PurchaseId!,
                  purchaseType: PurchaseType.FundedTree,
                  savePaymentMethod: input.SavePaymentMethod,
                  path: router.pathname,
                  // Forward the package tier (if present) so /success can
                  // render the package-specific thank-you screen.
                  ...(typeof router.query.package === 'string'
                    ? { package: router.query.package }
                    : {})
                }
              })}`
            }
          })

          if (result.error) {
            setProcessing(false)
            setSucceeded(false)
            notify({
              message: result.error.message!,
              type: NotificationType.error
            })
            await updatePurchaseError({
              variables: {
                input: {
                  PurchaseId: response.PurchaseId!,
                  SubscriptionId: response.SubscriptionId,
                  Error: result.error.message || 'Unknown error'
                }
              }
            })
          }
        } else
          notify({
            message: 'No Client Secret!',
            type: NotificationType.error
          })
      }

      const handlePayment = async () => {
        const { error: submitError } = await elements!.submit()
        if (submitError) {
          setProcessing(false)
          notify({
            message: submitError.message!,
            type: NotificationType.error
          })
          return
        }

        if (purchase) {
          await onCompleted(purchase)
        } else {
          await createPayment({
            variables: { input },
            onError,
            onCompleted: data => onCompleted(data.create_payment)
          })
        }
      }

      const handleGuestPayment = async () => {
        const { error: submitError } = await elements!.submit()
        if (submitError) {
          setProcessing(false)
          notify({
            message: submitError.message!,
            type: NotificationType.error
          })
          return
        }

        const paymentInput = await createGuestPaymentInput(input).catch(err => {
          onError(err)
          return null
        })
        if (!paymentInput) return
        if (purchase) {
          await onCompleted(purchase)
        } else {
          await createGuestPayment({
            variables: { input: paymentInput },
            onError,
            onCompleted: async data => {
              sendGuestDetailsToHubspot()
              await onCompleted(data.create_guest_payment)
            }
          })
        }
      }

      if (!user) return handleGuestPayment()
      return handlePayment()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      createPayment,
      elements,
      onSuccess,
      setDonateFormCache,
      setProcessing,
      setSucceeded,
      stripe
    ]
  )

  const handleInvoicePayment = useCallback(
    (input: CreatePaymentInput) => {
      createPayment({
        variables: {
          input
        },
        onCompleted: async response => {
          setProcessing(false)

          setSucceeded(true)

          const successPath = `${
            env.NEXT_PUBLIC_APP_DOMAIN
          }${paymentSuccessPath({
            queryParams: {
              token: response.create_payment?.Token,
              hash: response.create_payment?.Hash,
              purchaseId: response.create_payment?.PurchaseId,
              purchaseType: PurchaseType.FundedTree,
              path: router.pathname,
              // Forward the package tier (if present) so /success can render
              // the package-specific thank-you screen.
              ...(typeof router.query.package === 'string'
                ? { package: router.query.package }
                : {})
            }
          })}`
          await router.push(successPath)
        }
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createPayment, onSuccess, setDonateFormCache, setProcessing, setSucceeded]
  )

  const handleReceiptNameBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue('ReceiptName', e.target.value)
  }

  const onSubmit = async () => {
    if (await validateStep()) {
      setProcessing(true)
      setDonateFormCache(values)

      const {
        Amount,
        InvoiceAddress,
        PaymentMethod,
        PlantingRegionId,
        Quantity,
        QuantityOther,
        ReceiptEmail,
        ReceiptName,
        RegistryId,
        PublicName,
        PublicMessage,
        SavePaymentMethod
      } = values

      const plantingRegionId = isRegistryGiftForm
        ? registry?.Registry_by_pk?.RegionId
        : PlantingRegionId

      // Package flow (`/get-started → /donate`): ?package=<slug> carries the
      // tier and DonationType carries the cadence. Package payments — monthly
      // AND annual — are always recurring subscriptions, and the backend keys
      // off Tier/Interval to price + bill against the right Stripe Price.
      const tier =
        typeof router.query.package === 'string'
          ? router.query.package
          : undefined
      const packageFields = tier
        ? {
            Tier: tier,
            Interval: isAnnual ? 'annual' : 'monthly'
          }
        : {}

      if (
        PaymentMethod === PaymentMethodEnum.CreditCard ||
        PaymentMethod === 'SavedCreditCard'
      ) {
        handleCreditCardPayment({
          Amount,
          PlantingRegionId:
            plantingRegionId === 'choose_for_me' ? undefined : plantingRegionId,
          Quantity: parseInt(`${QuantityOther}`) || parseInt(`${Quantity}`),
          ReceiptEmail,
          ReceiptName,
          RegistryId,
          PaymentMethod,
          PublicName,
          PublicMessage,
          IsRecurring: isRecurring,
          SavePaymentMethod,
          ...getStoredUtmInput(),
          ...packageFields
        })
      } else if (PaymentMethod === PaymentMethodEnum.Invoice) {
        handleInvoicePayment({
          Amount,
          InvoiceAddress: InvoiceAddress as InvoiceAddress,
          PaymentMethod,
          PlantingRegionId:
            plantingRegionId === 'choose_for_me' ? null : plantingRegionId,
          Quantity: parseInt(`${QuantityOther}`) || parseInt(`${Quantity}`),
          ReceiptEmail,
          ReceiptName,
          RegistryId,
          PublicName,
          PublicMessage,
          ...getStoredUtmInput()
        })
      } else throw new Error('Unsupported payment type')
    }
  }

  const SubscriptionNote = useMemo(() => {
    if (
      isRecurring &&
      subscription?.Subscription.length &&
      !isSubmitting &&
      !processing
    ) {
      return (
        <Note className='mt-5 md:mt-10'>
          You already have an active subscription set up. You can update this
          within{' '}
          <InternalLink href={viewAccountBillingDetailsPath}>
            your payments page
          </InternalLink>
          .
        </Note>
      )
    }
    return null
  }, [isSubmitting, processing, subscription?.Subscription.length, isRecurring])

  let paymentMethodOptions = []
  paymentMethodOptions = [
    {
      label: user?.Account?.PaymentMethodDescriptor
        ? 'Different card, Google Pay, or Apple Pay'
        : 'Credit or debit card',
      value: PaymentMethodEnum.CreditCard,
      disabled: false
    }
  ]

  if (user?.Account?.PaymentMethodDescriptor) {
    paymentMethodOptions = [
      {
        label: `Saved card (${user.Account.PaymentMethodDescriptor})`,
        value: 'SavedCreditCard',
        disabled: !user
      },
      ...paymentMethodOptions
    ]
  }

  if (
    productPrice * parseInt(values.QuantityOther || values.Quantity, 10) >
      INVOICE_PAYMENT_MINIMUM_AMOUNT &&
    !isRecurring
  ) {
    paymentMethodOptions = [
      {
        label: 'By invoice',
        value: PaymentMethodEnum.Invoice,
        disabled: !user
      },
      ...paymentMethodOptions
    ]
  }

  let donationDetailsHeading = 'Donation details'
  if (values.DonationType === DonateFrequencyTab.Monthly) {
    donationDetailsHeading = 'Monthly donation details'
  } else if (isAnnual) {
    donationDetailsHeading = 'Annual donation details'
  }

  return (
    <>
      {!isRegistryGiftForm ? SubscriptionNote : null}
      {isRecurring &&
      subscription?.Subscription.length &&
      !isSubmitting &&
      !processing ? null : (
        <>
          <div className='mb-8'>
            <div className='mb-[10px]'>
              <TypographyRebrand variant='h4' color='forestDeep'>
                {donationDetailsHeading}
              </TypographyRebrand>
            </div>
            <div className='bg-linen p-5 text-forestDeep [&_*]:text-forestDeep!'>
              {user ? (
                <>
                  <dl className='grid gap-y-2 mb-4'>
                    <dt className='col-start-1 font-semibold mr-4'>
                      <span className='align-middle'>
                        Account name
                        {(memberships?.length || 0) > 1 ? (
                          <Tooltip
                            overlay={
                              <div className='text-center px-[18px] py-[10px]'>
                                Looks like you have multiple accounts. You can
                                switch accounts within the dropdown menu to the
                                top-right of the page.
                              </div>
                            }
                          >
                            <IoInformationCircle className='text-forestDeep w-5 h-5 inline ml-[10px]' />
                          </Tooltip>
                        ) : null}
                      </span>
                    </dt>
                    <dd className='col-start-2 text-right'>
                      {user?.Account?.Name}
                    </dd>
                  </dl>
                  <hr className='mb-4' />
                </>
              ) : null}
              <dl className='grid gap-y-2 mb-4'>
                <dt className='col-start-1 font-semibold mr-4'>No. of trees</dt>
                <dd className='col-start-2 text-right'>
                  {values.Quantity === 'other'
                    ? values.QuantityOther
                    : values.Quantity}
                </dd>
              </dl>
              <dl className='grid gap-y-2 mb-4'>
                <dt className='col-start-1 font-semibold mr-4'>Price</dt>
                <dd className='col-start-2 text-right'>
                  <Currency showCents amount={values.PricePerTree} />
                </dd>
              </dl>
              {values.RegistryId ||
              regionName === 'Where trees are needed most' ? null : (
                <dl className='grid gap-y-2 mb-4'>
                  <dt className='col-start-1 font-semibold mr-4'>
                    Preferred planting region
                  </dt>
                  <dd className='col-start-2 text-right'>{regionName}</dd>
                </dl>
              )}
              <hr />
              <dl className='grid gap-y-1 mt-4'>
                <dt className='col-start-1 font-semibold mr-4'>
                  Amount to pay
                </dt>
                <dd className='col-start-2 text-right'>
                  <Currency showCents amount={values.Amount} />
                </dd>
              </dl>
            </div>
          </div>

          {!user && (
            <Note type='infoNew'>
              Already have an account?{' '}
              <OnClickLink
                onClick={login}
                className='text-forestDeep! underline'
              >
                Sign in
              </OnClickLink>{' '}
              to add this donation to your total
            </Note>
          )}
          <PaymentContactDetails
            handleReceiptNameBlur={handleReceiptNameBlur}
            isRecurring={isRecurring}
            paymentMethod={values.PaymentMethod}
          />
          {isRecurring && (
            <Note type='infoNew'>
              <TypographyRebrand variant='paragraph' color='forestDeep'>
                Your next donation payment will be on {nextBillingDate}. You can
                change or cancel your {isAnnual ? 'annual' : 'monthly'} payment
                anytime from your account settings.
              </TypographyRebrand>
            </Note>
          )}
          {!user ? (
            <>
              <FormField
                id='Organisation'
                label='Organisation (optional)'
                labelClassName='text-forestDeep!'
                type={FormFieldTypes.text}
              />
              <FormField
                id='CommsOptIn'
                label=''
                formFieldClassName='[&_svg]:fill-lavenderBright! [&_span]:text-forestDeep!'
                checkboxLabel='Opt in to communications from Trees That Count'
                type={FormFieldTypes.checkbox}
              />
            </>
          ) : null}
          <div className='mt-8 mb-4'>
            <div className='mb-4'>
              <TypographyRebrand variant='h4' color='forestDeep'>
                Payment
              </TypographyRebrand>
            </div>
            {paymentMethodOptions.length > 1 && (
              <FormField
                id='PaymentMethod'
                label='Select payment method'
                labelClassName='text-forestDeep!'
                description={
                  !user ? (
                    <div>
                      Please{' '}
                      <OnClickLink
                        onClick={login}
                        className='text-forestDeep! underline'
                      >
                        sign in
                      </OnClickLink>{' '}
                      to pay by invoice
                    </div>
                  ) : (
                    ''
                  )
                }
                type={FormFieldTypes.radioGroup}
                options={paymentMethodOptions}
              />
            )}

            {values.PaymentMethod === PaymentMethodEnum.Invoice && (
              <>
                <TypographyRebrand variant='paragraph' color='forestDeep'>
                  <strong>Pay by invoice</strong>
                </TypographyRebrand>
                <FormField
                  id='InvoiceAddress.InvoiceAddress0'
                  label='Address Line 1'
                  type={FormFieldTypes.text}
                />
                <FormField
                  id='InvoiceAddress.InvoiceAddress1'
                  label='Address Line 2'
                  type={FormFieldTypes.text}
                />
                <div className='flex space-x-4'>
                  <FormField
                    id='InvoiceAddress.InvoiceCity'
                    label='City'
                    type={FormFieldTypes.text}
                  />
                  <FormField
                    id='InvoiceAddress.InvoicePostCode'
                    label='Post Code'
                    type={FormFieldTypes.text}
                  />
                </div>
                <FormField
                  id='InvoiceAddress.InvoiceCountry'
                  label='Country'
                  type={FormFieldTypes.text}
                />
              </>
            )}
            {values.PaymentMethod === PaymentMethodEnum.CreditCard && (
              <>
                <div className='mb-2'>
                  <TypographyRebrand variant='paragraph' color='forestDeep'>
                    <strong>Credit/Debit card</strong>
                  </TypographyRebrand>
                </div>
                <div className='mb-2'>
                  <TypographyRebrand
                    variant='paragraphSmall'
                    color='forestDeep'
                  >
                    Amount to pay: <Currency amount={values.Amount} />
                  </TypographyRebrand>
                </div>
                <StripePaymentElements
                  onChange={event => {
                    if (event.complete) sendTrackingEvent('add_payment_info')
                    setWalletPay(event.value.type !== 'card')
                  }}
                />

                {isRecurring ? (
                  <Note type='infoNew'>
                    <TypographyRebrand variant='paragraph' color='forestDeep'>
                      We&apos;ll store your card details through Stripe&apos;s
                      secure platform. You can change your payment details and
                      amount at any time.
                    </TypographyRebrand>
                  </Note>
                ) : (
                  <>
                    {!walletPay && (
                      <FormField
                        id='SavePaymentMethod'
                        label='Remember my details'
                        labelClassName='text-forestDeep!'
                        formFieldClassName='[&_svg]:fill-lavenderBright! [&_span]:text-forestDeep!'
                        checkboxLabel='Save my card details for a faster checkout next time'
                        type={FormFieldTypes.checkbox}
                        disabled={!user}
                      />
                    )}

                    {user && values.SavePaymentMethod && !walletPay && (
                      <Note>
                        {user.Account?.PaymentMethodDescriptor &&
                          values.SavePaymentMethod && (
                            <TypographyRebrand
                              variant='paragraph'
                              color='forestDeep'
                            >
                              Saving a new payment card will replace your
                              existing saved card (
                              {user.Account.PaymentMethodDescriptor}).{' '}
                            </TypographyRebrand>
                          )}
                        We&apos;ll store your payment method (credit/debit card)
                        through Stripe&apos;s secure platform. You&apos;ll be
                        able to change your details at any time to ensure your
                        donation goes smoothly.
                      </Note>
                    )}
                  </>
                )}
              </>
            )}
            {values.PaymentMethod === PaymentMethodEnum.SavedCreditCard && (
              <>
                <div className='mb-2'>
                  <TypographyRebrand variant='paragraph' color='forestDeep'>
                    <strong>Credit/Debit card</strong>
                  </TypographyRebrand>
                </div>
                <div className='mb-2'>
                  <TypographyRebrand
                    variant='paragraphSmall'
                    color='forestDeep'
                  >
                    Amount to pay: <Currency amount={values.Amount} />
                  </TypographyRebrand>
                </div>
              </>
            )}
          </div>

          <StepperButtons
            isSubmitDisabled={isSubmitDisabled}
            processing={processing}
            submitText='Make payment'
            onSubmit={onSubmit}
          />
        </>
      )}
    </>
  )
}
